import React from 'react'
import { Box, Button, Chip, Typography } from '@mui/material'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import useToastNotification from '../../hooks/useToastNotification'
import {
  SUPPORT_IMAGES_MAX,
  SUPPORT_IMAGE_MAX_SIZE_MB,
  SUPPORT_IMAGE_ACCEPTED_TYPES,
  validateSupportImages,
} from '../../utils/validations/supportValidations'

type SupportImagePickerProps = {
  images: File[]
  setImages: (images: File[]) => void
  disabled?: boolean
}

const SupportImagePicker = ({
  images,
  setImages,
  disabled = false,
}: SupportImagePickerProps) => {
  const { sendToastMessage } = useToastNotification()

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const addedImages = Array.from(e.target.files ?? [])
    // Reset so picking the same file again after removing it still fires onChange
    e.target.value = ''

    if (addedImages.length === 0) return

    const validationError = validateSupportImages(addedImages, images.length)

    if (validationError !== undefined) {
      sendToastMessage('error', validationError)
      return
    }

    setImages([...images, ...addedImages])
  }

  return (
    <Box textAlign="left">
      <Box display="flex" alignItems="center" gap={1.5}>
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={<AddPhotoAlternateIcon />}
          disabled={disabled || images.length >= SUPPORT_IMAGES_MAX}
        >
          Add images
          <input
            hidden
            multiple
            type="file"
            accept={SUPPORT_IMAGE_ACCEPTED_TYPES.join(', ')}
            onChange={handleAddImage}
          />
        </Button>
        <Typography variant="caption" color="text.secondary">
          {images.length}/{SUPPORT_IMAGES_MAX} &middot; JPEG, PNG or WebP, up to{' '}
          {SUPPORT_IMAGE_MAX_SIZE_MB} MB each
        </Typography>
      </Box>

      {images.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
          {images.map((image, index) => (
            <Chip
              key={`${image.name}-${index}`}
              label={image.name}
              size="small"
              disabled={disabled}
              onDelete={() => setImages(images.filter((_, i) => i !== index))}
              sx={{ maxWidth: 240 }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default SupportImagePicker
